/** @typedef {{ edition: string, title?: string, generatedAt?: string | null, available?: boolean, stale?: boolean }} EditionSummary */

export const LIVE_POLL_INTERVAL_MS = 300_000;

/**
 * Pin Hourly first; sort remaining report tabs by generatedAt DESC (pending last).
 * @param {Array<{ slug: string }>} timeSlots
 * @param {Map<string, EditionSummary>} summariesByEdition
 */
export function sortReportTabs(timeSlots, summariesByEdition) {
  const hourly = timeSlots.find((slot) => slot.slug === 'hourly');
  const reports = timeSlots.filter((slot) => slot.slug !== 'hourly');

  const sortedReports = [...reports].sort((a, b) => {
    const atA = parseGeneratedAt(summariesByEdition.get(a.slug)?.generatedAt);
    const atB = parseGeneratedAt(summariesByEdition.get(b.slug)?.generatedAt);
    if (atA == null && atB == null) return 0;
    if (atA == null) return 1;
    if (atB == null) return -1;
    return atB - atA;
  });

  return hourly ? [hourly, ...sortedReports] : sortedReports;
}

/** @param {string | null | undefined} value */
export function parseGeneratedAt(value) {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * @param {EditionSummary | undefined} summary
 * @param {Date} [now]
 * @returns {{ state: 'pending' | 'stale' | 'fresh', freshness: number }}
 */
export function tabFreshnessMeta(summary, now = new Date()) {
  if (!summary?.available || !summary.generatedAt) {
    return { state: 'pending', freshness: 0 };
  }
  if (summary.stale) {
    return { state: 'stale', freshness: 0 };
  }
  const generatedMs = parseGeneratedAt(summary.generatedAt);
  if (generatedMs == null) {
    return { state: 'pending', freshness: 0 };
  }
  const ageHours = (now.getTime() - generatedMs) / 3_600_000;
  const freshness = Math.max(0, Math.min(1, 1 - ageHours / 24));
  return { state: 'fresh', freshness };
}

/** @param {EditionSummary[]} summaries */
export function summarizeLastSeen(summaries) {
  /** @type {Record<string, string>} */
  const map = {};
  summaries.forEach((summary) => {
    if (summary.generatedAt) {
      map[summary.edition] = summary.generatedAt;
    }
  });
  return map;
}

/**
 * @param {EditionSummary[]} summaries
 * @param {Record<string, string>} lastSeen
 * @param {string} activeSlug
 */
export function activeEditionNeedsRefresh(summaries, lastSeen, activeSlug) {
  const summary = summaries.find((item) => item.edition === activeSlug);
  if (!summary?.generatedAt) return false;

  const previous = lastSeen[activeSlug];
  if (!previous) {
    return Boolean(summary.available);
  }

  const prevMs = parseGeneratedAt(previous);
  const nextMs = parseGeneratedAt(summary.generatedAt);
  if (prevMs == null || nextMs == null) return false;
  return nextMs > prevMs;
}

/**
 * @param {string} apiBase
 * @returns {Promise<EditionSummary[]>}
 */
export async function fetchEditionSummaries(apiBase) {
  const response = await fetch(`${apiBase}/news/editions`, {
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}

/** @param {EditionSummary[]} summaries */
export function summariesByEditionMap(summaries) {
  return new Map(summaries.map((summary) => [summary.edition, summary]));
}
